/**
 * Diagnostics Panel Component
 * Dev-only panel for diagnostics and telemetry
 */

/* global document */

import { VERSION } from "../shared/version.generated";
import { getPublicConfig, getPublicConfigSyncUnsafe } from "../excel/config/getPublicConfig";
import { logger } from "../shared/logger";
import type { AppPublicConfig } from "../shared/config/types";
import {
  diagnosticsStore,
  copyDiagnosticsToClipboard,
  type DiagnosticsSnapshot,
} from "../shared/diagnostics";

interface BackendVersion {
  version: {
    appName: string;
    env: "dev" | "prod";
    gitSha: string;
    buildTime: string;
  };
}

interface BackendHealth {
  ok: boolean;
  version: {
    appName: string;
    env: "dev" | "prod";
    gitSha: string;
    buildTime: string;
  };
  time: string;
}

let pollInterval: number | null = null;
let lastSnapshot: DiagnosticsSnapshot | null = null;

/**
 * Initialize and manage the Diagnostics panel
 */
export async function initializeDiagnosticsPanel(): Promise<void> {
  // Load client config to check if panel should be shown
  // getPublicConfig() always returns defaults if fetch fails, so this should never throw
  // But we add a fallback for extra robustness
  let clientConfig: AppPublicConfig;
  try {
    clientConfig = await getPublicConfig();
  } catch (error) {
    // This should never happen since getPublicConfig() always returns defaults
    // But if it does, fall back to sync unsafe defaults
    logger.error("Diagnostics", "failed to load client config", error);
    clientConfig = getPublicConfigSyncUnsafe();
  }

  // Gate rendering: only show in dev with feature flag enabled
  // Double-check: also verify VERSION.env is dev (build-time truth)
  if (clientConfig.env !== "dev" || VERSION.env !== "dev") {
    return;
  }

  if (clientConfig.public.featureFlags.diagnostics !== true) {
    return;
  }

  // Panel should be shown - set up UI
  const panelEl = document.getElementById("diagnostics-panel");
  if (!panelEl) {
    logger.error("Diagnostics", "diagnostics-panel element not found");
    return;
  }

  panelEl.style.display = "block";

  // Initialize diagnostics store (idempotent, safe even if already called in proxyClient)
  await diagnosticsStore.init();

  // Wire up buttons
  const copyBtn = document.getElementById("diagnostics-copy-btn");
  if (copyBtn) {
    copyBtn.onclick = () => {
      void (async () => {
        const res = await copyDiagnosticsToClipboard();
        renderCopyStatus(
          res.ok ? "Copied." : "Copy failed: " + (res.error || "unknown")
        );
      })();
    };
  }

  const clearBtn = document.getElementById("diagnostics-clear-btn");
  if (clearBtn) {
    clearBtn.onclick = () => {
      void (async () => {
        await diagnosticsStore.clear();
        updateDiagnosticsUI();
      })();
    };
  }

  const healthCheckBtn = document.getElementById("diagnostics-health-check-btn");
  if (healthCheckBtn) {
    healthCheckBtn.onclick = () => {
      void performHealthCheck(clientConfig.public.apiBaseUrl);
    };
  }

  // Initial update
  updateDiagnosticsUI();
  
  // Fetch backend version on initialization
  void fetchBackendVersion(clientConfig.public.apiBaseUrl);

  // Start polling (pause when panel hidden)
  if (pollInterval !== null) {
    clearInterval(pollInterval);
  }
  pollInterval = window.setInterval(() => {
    if (panelEl && panelEl.offsetParent !== null) {
      void updateDiagnosticsUI();
    }
  }, 1000);
}

/**
 * Update UI with current diagnostics snapshot
 * Uses diff updates to minimize DOM churn
 * Also refreshes from storage to pick up diagnostics from functions context
 */
async function updateDiagnosticsUI(): Promise<void> {
  // Re-initialize to load latest data from storage (includes data from functions context)
  await diagnosticsStore.init();
  
  // Force merge from storage before getting snapshot
  await diagnosticsStore.mergeStatsFromStorage();
  
  const snapshot = diagnosticsStore.getSnapshot();

  // Diff check: only update if snapshot changed
  if (lastSnapshot && JSON.stringify(lastSnapshot) === JSON.stringify(snapshot)) {
    return;
  }
  lastSnapshot = snapshot;

  // Frontend version
  const frontendGitShaEl = document.getElementById("diagnostics-frontend-gitsha");
  if (frontendGitShaEl) {
    frontendGitShaEl.textContent = VERSION.gitSha;
  }

  const frontendBuildTimeEl = document.getElementById("diagnostics-frontend-buildtime");
  if (frontendBuildTimeEl) {
    try {
      const date = new Date(VERSION.buildTime);
      frontendBuildTimeEl.textContent = date.toLocaleString();
    } catch (e) {
      frontendBuildTimeEl.textContent = VERSION.buildTime;
    }
  }

  const envEl = document.getElementById("diagnostics-env");
  if (envEl) {
    envEl.textContent = VERSION.env;
  }

  // API Base URL
  const apiBaseUrlEl = document.getElementById("diagnostics-api-base-url");
  if (apiBaseUrlEl) {
    void (async () => {
      try {
        const config = await getPublicConfig();
        apiBaseUrlEl.textContent = config.public.apiBaseUrl;
      } catch (error) {
        apiBaseUrlEl.textContent = "Error loading";
      }
    })();
  }

  const officeHostEl = document.getElementById("diagnostics-office-host");
  if (officeHostEl) {
    officeHostEl.textContent = snapshot.office.host;
  }

  const officePlatformEl = document.getElementById("diagnostics-office-platform");
  if (officePlatformEl) {
    officePlatformEl.textContent = snapshot.office.platform;
  }

  // Session started
  const sessionStartedEl = document.getElementById("diagnostics-session-started");
  if (sessionStartedEl) {
    try {
      const date = new Date(snapshot.session.started_at);
      sessionStartedEl.textContent = date.toLocaleString();
    } catch (e) {
      sessionStartedEl.textContent = snapshot.session.started_at;
    }
  }

  // Last request ID
  const lastRequestIdEl = document.getElementById("diagnostics-last-request-id");
  if (lastRequestIdEl) {
    lastRequestIdEl.textContent = snapshot.last_request?.request_id || "-";
  }

  // Last error
  const lastErrorEl = document.getElementById("diagnostics-last-error");
  if (lastErrorEl) {
    if (snapshot.last_error) {
      try {
        const date = new Date(snapshot.last_error.at_iso);
        // Format error message more clearly
        let errorText = date.toLocaleString() + " [" + snapshot.last_error.category + "] ";
        
        // For ProxyClientError messages, extract just the error message part
        // (remove "ProxyClientError: " prefix if present)
        let errorMsg = snapshot.last_error.message;
        if (errorMsg.startsWith("ProxyClientError: ")) {
          errorMsg = errorMsg.substring("ProxyClientError: ".length);
        }
        
        errorText += errorMsg;
        
        if (snapshot.last_error.request_id) {
          errorText += " (req: " + snapshot.last_error.request_id + ")";
        }
        
        lastErrorEl.textContent = errorText;
      } catch (e) {
        lastErrorEl.textContent =
          "[" +
          snapshot.last_error.category +
          "] " +
          snapshot.last_error.message;
      }
    } else {
      lastErrorEl.textContent = "-";
    }
  }

  // Top ops table (limit 10)
  const statsTbodyEl = document.getElementById("diagnostics-stats-tbody");
  if (statsTbodyEl) {
    statsTbodyEl.innerHTML = "";
    const sortedOps = Object.entries(snapshot.stats)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 10);

    for (const [op_key, stats] of sortedOps) {
      const row = document.createElement("tr");
      row.innerHTML =
        "<td style='padding: 4px;'>" +
        escapeHtml(op_key) +
        "</td>" +
        "<td style='text-align: right; padding: 4px;'>" +
        stats.count +
        "</td>" +
        "<td style='text-align: right; padding: 4px;'>" +
        stats.avg_ms +
        "</td>" +
        "<td style='text-align: right; padding: 4px;'>" +
        stats.max_ms +
        "</td>" +
        "<td style='text-align: right; padding: 4px;'>" +
        stats.in_flight +
        "</td>";
      statsTbodyEl.appendChild(row);
    }
  }

  // Recent calls table (limit 10)
  const recentTbodyEl = document.getElementById("diagnostics-recent-tbody");
  if (recentTbodyEl) {
    recentTbodyEl.innerHTML = "";
    const recentCalls = snapshot.recent.slice(0, 10);

    for (const call of recentCalls) {
      const row = document.createElement("tr");
      try {
        const date = new Date(call.at_iso);
        const timeStr = date.toLocaleTimeString();
        const cacheVal = call.cache || "none";
        const cacheClass = "diag-cache-" + cacheVal;
        const sessionBase = call.sessionCache || "none";
        const sessionVal =
          sessionBase === "miss" &&
          typeof call.sessionJoinCount === "number" &&
          call.sessionJoinCount > 0
            ? "miss (+" + call.sessionJoinCount + ")"
            : sessionBase;
        const sessionClass = "diag-session-" + sessionBase;
        const cacheKey = call.cacheKey ? escapeHtml(call.cacheKey) : "-";
        const cacheBucket = call.cacheBucket != null && call.cacheBucket !== "" ? escapeHtml(call.cacheBucket) : "-";
        row.innerHTML =
          "<td style='padding: 4px;'>" +
          escapeHtml(timeStr) +
          "</td>" +
          "<td style='padding: 4px;'>" +
          escapeHtml(call.op_key) +
          "</td>" +
          "<td style='text-align: center; padding: 4px;'>" +
          (call.ok ? "✓" : "✗") +
          "</td>" +
          "<td style='text-align: right; padding: 4px;'>" +
          call.ms +
          "</td>" +
          "<td style='padding: 4px; font-size: 0.85em;'>" +
          escapeHtml(call.request_id || "-") +
          "</td>" +
          "<td class='" +
          cacheClass +
          "' style='padding: 4px;'>" +
          cacheVal +
          "</td>" +
          "<td style='padding: 4px; font-size: 0.85em;'>" +
          cacheBucket +
          "</td>" +
          "<td class='diag-cache-key-cell' style='padding: 4px;' title='" +
          (call.cacheKey ? escapeHtml(call.cacheKey) : "") +
          "'>" +
          cacheKey +
          "</td>" +
          "<td class='" +
          sessionClass +
          "' style='padding: 4px;'>" +
          sessionVal +
          "</td>";
      } catch (e) {
        const cacheVal = call.cache || "none";
        const cacheClass = "diag-cache-" + cacheVal;
        const sessionBase = call.sessionCache || "none";
        const sessionVal =
          sessionBase === "miss" &&
          typeof call.sessionJoinCount === "number" &&
          call.sessionJoinCount > 0
            ? "miss (+" + call.sessionJoinCount + ")"
            : sessionBase;
        const sessionClass = "diag-session-" + sessionBase;
        const cacheKey = call.cacheKey ? escapeHtml(call.cacheKey) : "-";
        const cacheBucket = call.cacheBucket != null && call.cacheBucket !== "" ? escapeHtml(call.cacheBucket) : "-";
        row.innerHTML =
          "<td style='padding: 4px;'>" +
          escapeHtml(call.at_iso) +
          "</td>" +
          "<td style='padding: 4px;'>" +
          escapeHtml(call.op_key) +
          "</td>" +
          "<td style='text-align: center; padding: 4px;'>" +
          (call.ok ? "✓" : "✗") +
          "</td>" +
          "<td style='text-align: right; padding: 4px;'>" +
          call.ms +
          "</td>" +
          "<td style='padding: 4px; font-size: 0.85em;'>" +
          escapeHtml(call.request_id || "-") +
          "</td>" +
          "<td class='" +
          cacheClass +
          "' style='padding: 4px;'>" +
          cacheVal +
          "</td>" +
          "<td style='padding: 4px; font-size: 0.85em;'>" +
          cacheBucket +
          "</td>" +
          "<td class='diag-cache-key-cell' style='padding: 4px;' title='" +
          (call.cacheKey ? escapeHtml(call.cacheKey) : "") +
          "'>" +
          cacheKey +
          "</td>" +
          "<td class='" +
          sessionClass +
          "' style='padding: 4px;'>" +
          sessionVal +
          "</td>";
      }
      recentTbodyEl.appendChild(row);
    }
  }
}

/**
 * Render copy status message
 */
function renderCopyStatus(message: string): void {
  const statusEl = document.getElementById("diagnostics-copy-status");
  if (statusEl) {
    statusEl.textContent = message;
    statusEl.style.display = "block";
    // Hide after 3 seconds
    setTimeout(() => {
      statusEl.style.display = "none";
    }, 3000);
  }
}

/**
 * Fetch backend version
 */
async function fetchBackendVersion(apiBaseUrl: string): Promise<void> {
  try {
    const url = apiBaseUrl.replace(/\/$/, "") + "/version";
    const response = await fetch(url);
    if (!response.ok) {
      const gitShaEl = document.getElementById("diagnostics-backend-gitsha");
      const buildTimeEl = document.getElementById("diagnostics-backend-buildtime");
      const envEl = document.getElementById("diagnostics-backend-env");
      if (gitShaEl) gitShaEl.textContent = "Error: " + response.status;
      if (buildTimeEl) buildTimeEl.textContent = "-";
      if (envEl) envEl.textContent = "-";
      return;
    }
    const data = (await response.json()) as BackendVersion;
    const v = data.version;

    const gitShaEl = document.getElementById("diagnostics-backend-gitsha");
    const buildTimeEl = document.getElementById("diagnostics-backend-buildtime");
    const envEl = document.getElementById("diagnostics-backend-env");

    if (gitShaEl) gitShaEl.textContent = v.gitSha;
    if (buildTimeEl) {
      try {
        const date = new Date(v.buildTime);
        buildTimeEl.textContent = date.toLocaleString();
      } catch (e) {
        buildTimeEl.textContent = v.buildTime;
      }
    }
    if (envEl) envEl.textContent = v.env;
  } catch (error) {
    const gitShaEl = document.getElementById("diagnostics-backend-gitsha");
    const buildTimeEl = document.getElementById("diagnostics-backend-buildtime");
    const envEl = document.getElementById("diagnostics-backend-env");
    const errorMsg = error instanceof Error ? error.message : String(error);
    if (gitShaEl) gitShaEl.textContent = "Error: " + errorMsg;
    if (buildTimeEl) buildTimeEl.textContent = "-";
    if (envEl) envEl.textContent = "-";
  }
}

/**
 * Perform health check
 */
async function performHealthCheck(apiBaseUrl: string): Promise<void> {
  const statusEl = document.getElementById("diagnostics-health-status");
  if (statusEl) {
    statusEl.textContent = "Checking...";
  }

  const startTime = Date.now();
  try {
    const url = apiBaseUrl.replace(/\/$/, "") + "/health";
    const response = await fetch(url);
    const endTime = Date.now();
    const duration = endTime - startTime;

    if (!response.ok) {
      if (statusEl) {
        statusEl.textContent = "Error: " + response.status + " (" + duration + "ms)";
      }
      return;
    }

    const data = (await response.json()) as BackendHealth;
    if (statusEl) {
      statusEl.textContent =
        (data.ok ? "OK" : "FAIL") + " - " + duration + "ms - " + new Date(data.time).toLocaleTimeString();
    }
  } catch (error) {
    const endTime = Date.now();
    const duration = endTime - startTime;
    const errorMsg = error instanceof Error ? error.message : String(error);
    if (statusEl) {
      statusEl.textContent = "Error: " + errorMsg + " (" + duration + "ms)";
    }
  }
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}
